import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../utils/cn';
import usePerformanceOptimization from '../../hooks/usePerformanceOptimization';
import Button from '../ui/Button';

const HeroSection = ({
  title,
  subtitle,
  description,
  primaryCTA,
  secondaryCTA,
  backgroundClass = 'bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900',
  textColor = 'text-white',
  className = '',
  children,
  animate = true
}) => {
  const { shouldReduceAnimations, getOptimizedProps } = usePerformanceOptimization();

  const heroVariants = {
    initial: { opacity: 0, y: 30 },
    animate: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const HeroComponent = animate && !shouldReduceAnimations ? motion.section : 'section';
  const animationProps = animate && !shouldReduceAnimations 
    ? getOptimizedProps({
        variants: heroVariants,
        initial: "initial",
        animate: "animate"
      })
    : {};

  return (
    <HeroComponent
      className={cn(
        'pt-24 pb-12 relative overflow-hidden',
        backgroundClass,
        className
      )}
      {...animationProps}
    >
      {/* Background Effects */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 left-10 w-64 h-64 bg-brand-cyan/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-brand-purple/20 rounded-full blur-3xl animate-pulse" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center">
          {title && (
            <h1 className={cn(
              'text-5xl md:text-6xl font-bold mb-6 font-heading',
              textColor
            )}>
              {title}
            </h1>
          )}
          
          {subtitle && (
            <h2 className={cn(
              'text-2xl md:text-3xl font-semibold mb-4 font-heading',
              textColor
            )}>
              {subtitle}
            </h2>
          )}
          
          {description && (
            <p className={cn(
              'text-xl max-w-4xl mx-auto mb-8 font-body',
              textColor.replace('text-white', 'text-white/90')
            )}>
              {description}
            </p>
          )}
          
          {(primaryCTA || secondaryCTA) && (
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {primaryCTA && (
                <Button
                  variant="default"
                  size="lg"
                  className="px-8 py-4 glass-morph bg-brand-cyan/20 hover:bg-brand-cyan/30 text-white rounded-xl font-semibold border border-brand-cyan/50 neon-glow transition-all duration-300 backdrop-blur-md"
                  {...primaryCTA}
                >
                  {primaryCTA.text}
                </Button>
              )}
              
              {secondaryCTA && (
                <Button
                  variant="outline"
                  size="lg"
                  className="px-8 py-4 glass-morph border-2 border-brand-purple/50 text-white rounded-xl font-semibold hover:bg-brand-purple/20 hover:border-brand-purple transition-all duration-300 backdrop-blur-md"
                  {...secondaryCTA}
                >
                  {secondaryCTA.text}
                </Button>
              )}
            </div>
          )}
          
          {children}
        </div>
      </div>
    </HeroComponent>
  );
};

export default HeroSection;